'use client';

import { useEffect, useRef } from 'react';
import type { CSSProperties } from 'react';
import { bebasNeue, geistMono } from 'utils/fonts';
import type { IllustratedFinish } from 'content/gifting/voucherConfig';
import {
  ILLUS_CONFIG,
  ILLUS_FS,
  ILLUS_VS,
} from 'components/gifting/visuals/illustratedCardShader';
import {
  createFullScreenProgram,
  drawFullScreen,
  loseGl,
} from 'components/gifting/visuals/glProgram';
import { usePrefersReducedMotion } from 'hooks/media/usePrefersReducedMotion';
import { voucherAssetUrl } from '../assets';
import { ArtCardFace } from './ArtCardFace';
import { attachCardTilt } from './cardTilt';
import type { CardProps } from './types';
import styles from './ArtCard.module.css';

// Label ink per illustration, from the design.
const INK: Record<IllustratedFinish, CSSProperties> = {
  birthday: {
    '--tint': 'rgba(24,44,98,.78)',
    '--hi': 'rgba(255,255,255,.55)',
    '--sh': 'rgba(10,20,50,.35)',
  } as CSSProperties,
  baby: {
    '--tint': 'rgba(62,80,56,.8)',
    '--hi': 'rgba(255,255,255,.6)',
    '--sh': 'rgba(40,50,30,.3)',
  } as CSSProperties,
  diwali: {
    '--tint': 'rgba(255,222,165,.9)',
    '--hi': 'rgba(255,240,210,.25)',
    '--sh': 'rgba(10,5,30,.6)',
  } as CSSProperties,
  holidays: {
    '--tint': 'rgba(246,232,205,.88)',
    '--hi': 'rgba(255,255,255,.2)',
    '--sh': 'rgba(0,20,10,.6)',
  } as CSSProperties,
};

type Props = CardProps & { finish: IllustratedFinish };

/**
 * Illustrated voucher card: the print lit by a streak that follows the pointer over the
 * stage (the card's parent), with the picture's own flames, twinkles and breeze.
 * Without WebGL the illustration shows as a plain background.
 */
export const IllustratedCard = ({
  finish,
  title,
  subtitle,
  label,
  amount,
  className,
}: Props) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const src = voucherAssetUrl(`illus-${finish}.webp`);
  const rm = usePrefersReducedMotion();

  useEffect(() => {
    const card = cardRef.current;
    if (!card?.parentElement) return;
    const cfg = ILLUS_CONFIG[finish];

    const canvas = document.createElement('canvas');
    canvas.className = styles.canvas;
    canvas.setAttribute('aria-hidden', 'true');
    // preserveDrawingBuffer: CardStage samples this canvas to morph between cards.
    const gl = canvas.getContext('webgl', {
      antialias: true,
      preserveDrawingBuffer: true,
    });
    if (!gl) return;
    const prog = createFullScreenProgram(gl, ILLUS_VS, ILLUS_FS);
    if (!prog) {
      loseGl(gl);
      return;
    }
    const U = (n: string) => gl.getUniformLocation(prog, n);

    const g = new Array(12).fill(0);
    const gc = new Array(12).fill(0);
    cfg.glow.forEach((v, i) => {
      g.splice(i * 4, 4, v[0], v[1], v[2], v[3]);
      gc.splice(i * 4, 4, v[4], v[5], v[6], v[7]);
    });

    const st = { tx: 0, ty: 0, h: 0, sw: rm ? 0 : -0.7, px: 0.5, py: 0.5 };
    const T0 = performance.now();
    let t0 = T0;
    let ready = false;
    let iw = 1;
    let ih = 1;

    const render = (t: number) => {
      if (!ready) return;
      gl.uniform1f(U('uT'), t);
      gl.uniform2f(U('uTilt'), st.tx, st.ty);
      gl.uniform1f(U('uHolo'), st.h);
      gl.uniform1f(U('uSw'), st.sw);
      gl.uniform2f(U('uPtr'), st.px, st.py);
      drawFullScreen(gl);
    };

    const size = () => {
      if (!ready) return;
      const dpr = Math.min((window.devicePixelRatio || 1) * 1.25, 2.5);
      const w = Math.max(2, Math.round(card.clientWidth * dpr));
      const h = Math.max(2, Math.round(card.clientHeight * dpr));
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
      // Cover-crop the illustration, with a little slack so it can drift.
      const k = 0.95;
      const C = w / h;
      const A = iw / ih;
      const [sx, sy] = A >= C ? [(k * C) / A, k] : [k, (k * A) / C];
      gl.uniform2f(U('uRes'), w, h);
      gl.uniform1f(U('uDpr'), dpr);
      gl.uniform4f(U('uCrop'), cfg.cx ?? 0.5, cfg.cy ?? 0.5, sx, sy);
      gl.uniform1f(U('uImgA'), A);
      gl.uniform3fv(U('uLight'), cfg.light as unknown as number[]);
      gl.uniform1f(U('uStr'), cfg.str);
      gl.uniform1f(U('uFloor'), cfg.floor);
      gl.uniform1f(U('uGam'), cfg.gam ?? 1);
      gl.uniform1f(U('uSway'), rm ? 0 : cfg.sway);
      gl.uniform1i(U('uImg'), 0);
      gl.uniform4fv(U('uG'), g);
      gl.uniform4fv(U('uGC'), gc);
      gl.uniform4f(U('uSp'), cfg.spark[0], cfg.spark[1], 0, 0);
      gl.uniform3f(U('uSpC'), cfg.spark[2], cfg.spark[3], cfg.spark[4]);
      render(rm ? 0 : (performance.now() - T0) / 1000);
    };

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      iw = img.naturalWidth;
      ih = img.naturalHeight;
      gl.bindTexture(gl.TEXTURE_2D, gl.createTexture());
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGB, gl.RGB, gl.UNSIGNED_BYTE, img);
      ready = true;
      card.prepend(canvas);
      size();
      card.dataset.gl = 'on';
      t0 = performance.now();
    };
    img.src = src;

    const ro = new ResizeObserver(size);
    ro.observe(card);

    // Hover: the card turns toward the pointer; streak, foil and glow follow.
    const MAXR = 5;
    let tx = 0;
    let ty = 0;
    let gx = 0;
    let gy = 0;
    let hov = false;

    let visible = true;
    const io = new IntersectionObserver(
      ([e]) => {
        visible = e.isIntersecting;
      },
      { rootMargin: '100px' },
    );
    io.observe(card);

    let raf = 0;
    const frame = (now: number) => {
      raf = requestAnimationFrame(frame);
      if (!visible || !ready) return;
      const k = hov ? 0.08 : 0.045;
      tx += (gx * 0.6 - tx) * k;
      ty += (gy * 0.6 - ty) * k;
      const hh = Math.min(1, Math.hypot(tx, ty) * 1.1 + (hov ? 0.25 : 0));
      st.h += (hh - st.h) * 0.1;
      st.tx = tx;
      st.ty = ty;
      st.px = 0.5 + tx * 0.8;
      st.py = 0.5 + ty * 0.8;
      // Arrival: the streak sweeps across once.
      const a = Math.min(1, (now - t0) / 2400);
      st.sw = -0.7 * Math.pow(1 - a, 3);
      const moving = Math.abs(tx) + Math.abs(ty) + st.h > 0.003;
      card.style.transform = moving
        ? `rotateX(${(-ty * MAXR).toFixed(2)}deg) rotateY(${(tx * MAXR).toFixed(
            2,
          )}deg) translateZ(${(st.h * 5).toFixed(1)}px)`
        : '';
      card.style.boxShadow = moving
        ? `0 2px 4px rgba(0,0,0,.12),${(-tx * 8).toFixed(1)}px ${(
            18 -
            ty * 4 +
            st.h * 4
          ).toFixed(1)}px ${(28 + st.h * 8).toFixed(0)}px -14px rgba(0,0,0,${(
            0.28 +
            st.h * 0.06
          ).toFixed(2)})`
        : '';
      render((now - T0) / 1000);
    };
    // Pointer over the whole stage; this loop eases the turn and owns the transform.
    const detachTilt = rm
      ? undefined
      : attachCardTilt(card, {
          track: 'stage',
          onMove: (x, y) => {
            gx = (x - 0.5) * 2;
            gy = (y - 0.5) * 2;
            hov = true;
          },
          onLeave: () => {
            hov = false;
            gx = 0;
            gy = 0;
          },
        });
    if (!rm) raf = requestAnimationFrame(frame);

    return () => {
      cancelAnimationFrame(raf);
      detachTilt?.();
      ro.disconnect();
      io.disconnect();
      img.onload = null;
      loseGl(gl);
      canvas.remove();
      delete card.dataset.gl;
      card.style.transform = '';
      card.style.boxShadow = '';
    };
  }, [finish, src, rm]);

  return (
    <div
      ref={cardRef}
      className={`${styles.card} ${bebasNeue.variable} ${geistMono.variable} ${
        className ?? ''
      }`}
      style={{ ...INK[finish], '--bg': `url(${src})` } as CSSProperties}
      role="img"
      aria-label={label}
    >
      <ArtCardFace title={title} subtitle={subtitle} amount={amount} />
    </div>
  );
};
